import * as React from 'react';
import { connect } from 'react-redux';
import { UilMultiply, UilExclamationCircle } from '@iconscout/react-unicons';
import Select from 'react-select';
import { IApplicationState } from '../../../../../../reducers';
import { getAllOptionPacks } from '../../../../../../services/optionpack.service';
import { OptionData, ProjectLanguage, Question } from '@nexxt/common/types';

interface OptionLibraryProps {
    languages: ProjectLanguage[];
    didFetchOptionPack: boolean;
    optionpacks: any[];
    question: Question;
    disabled: boolean;
    setQuestionAsQuestion: (question: Question) => void;
    getAllOptionPacks: () => Promise<void>;
    onClose: () => void;
    toggleOptionLoader: (toggle: boolean) => void;
}

const OptionLibrary: React.FC<OptionLibraryProps> = ({
    languages,
    question,
    optionpacks,
    didFetchOptionPack,
    disabled,
    setQuestionAsQuestion,
    getAllOptionPacks,
    onClose,
    toggleOptionLoader
}: OptionLibraryProps) => {
    const [optionpackLanguage, setOptionpackLanguage] = React.useState([]);
    React.useEffect(() => {
        if (!didFetchOptionPack) {
            getAllOptionPacks();
        }
    }, []);

    React.useEffect(() => {
        if (question.optionSettings?.optionpackId) {
            const optionPack = optionpacks.find(
                (opt) => opt.id === question.optionSettings?.optionpackId
            );
            optionPack &&
                setOptionpackLanguage(
                    optionPack?.languages?.map((l) => l.value)
                );
        }
    }, [optionpacks?.length]);
    const selectOptionLibrary = (optionpack) => {
        toggleOptionLoader(true);
        const { availableOptions, languages, optionType, optionSettings } =
            optionpack;
        const currOptionIds = question.options
            .map((opt) => opt.id)
            .filter((x) => x);
        setOptionpackLanguage(languages.map((l) => l.value));
        setQuestionAsQuestion({
            ...question,
            selectedOptionType: optionType,
            random: false,
            options: availableOptions.map((opt: OptionData) => ({
                ...opt,
                id: null,
                randomizeOption: false
            })),
            deletedOptionIDs: [
                ...(question.deletedOptionIDs || []),
                ...currOptionIds
            ],
            showNotApplicableOption: optionSettings?.showNotApplicableOption,
            notApplicableTexts: optionSettings?.notApplicableTexts,
            optionSettings: {
                ...question.optionSettings,
                optionpackId: optionpack.value,
                showOther: optionSettings?.showOther,
                showSpecify: optionSettings?.showSpecify,
                otherOption: optionSettings?.otherOption
            }
        });
        toggleOptionLoader(false);
    };
    const selectedOptionpack = optionpacks?.find(
        (opt) => opt.value === question.optionSettings?.optionpackId
    );
    const missingLanguages = languages.filter(
        (l) => !optionpackLanguage.includes(l.value)
    );
    return (
        <div className="options--container--body--piping--container">
            <div className="options--container--body--piping--wrapper">
                <span>FROM OPTION LIBRARY </span>
                <Select
                    className="options--container--body--piping--select"
                    classNamePrefix="piping"
                    options={optionpacks}
                    onChange={selectOptionLibrary}
                    value={selectedOptionpack}
                    isDisabled={disabled}
                />
                {question.optionSettings.optionpackId &&
                missingLanguages?.length ? (
                    <span>
                        <UilExclamationCircle
                            size={20}
                            style={{ display: 'inline-block' }}
                        />
                        Note: you will need to add translations for{' '}
                        {missingLanguages.map((language, index) => (
                            <React.Fragment key={language.value}>
                                <span>{language.label}</span>
                                {index < missingLanguages.length - 1 && (
                                    <span> / </span>
                                )}
                            </React.Fragment>
                        ))}
                    </span>
                ) : null}
            </div>
            {!disabled && (
                <button
                    className="options--container--body--piping--close"
                    onClick={onClose}
                >
                    {' '}
                    <UilMultiply size={20} />
                </button>
            )}
        </div>
    );
};

const mapStateToProps = ({
    optionpackReducer,
    projectReducer
}: IApplicationState) => {
    return {
        languages: projectReducer.currentProject.languages,
        didFetchOptionPack: optionpackReducer.initialFetch,
        optionpacks: optionpackReducer.optionpacks
    };
};

const mapDispatchToProps = (dispatch) => ({
    getAllOptionPacks: () => dispatch(getAllOptionPacks())
});

export default connect(mapStateToProps, mapDispatchToProps)(OptionLibrary);
